/* eslint-disable no-shadow */
// hooks/useDocumentHandlers.js
import { useCallback } from 'react';
import {  validateFile } from '../utils/utils';
import { API } from '../apis/api';
import { decryptDocumentUrl } from '../utils/cryptoUtils';


 const useDocumentHandlers = (
  documentEntries,
  setDocumentEntries,
  ticketId,
  ticketCode,
  showNotification,
  userId
) => {
  const handleDocumentChange = useCallback((index, field, value) => {
    setDocumentEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      if (field === null && typeof value === "object") {
        newEntries[index] = { ...value };
      } else {
        newEntries[index] = { ...newEntries[index], [field]: value };
      }
      return newEntries;
    });
  }, [setDocumentEntries]);

  const handleDocumentFileChange = useCallback(async (index, files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const newEntries = [...documentEntries];
    
    try {
      validateFile(file);
      if (!newEntries[index].documentType) {
        showNotification("Please select a document type first", "error");
        return;
      }
      if (newEntries[index].documentCode) {
        showNotification("File already uploaded for this document", "warning");
        return;
      }
      newEntries[index].file = file;
      newEntries[index].isUploading = false;
      setDocumentEntries(newEntries);
      showNotification(
        "File selected successfully. Click upload to proceed.",
        "success"
      );
    } catch (fileError) {
      console.error("File handling error:", fileError);
      const updatedEntries = [...documentEntries];
      updatedEntries[index].file = null;
      updatedEntries[index].isUploading = false;
      setDocumentEntries(updatedEntries);
      let errorMessage = "Failed to process file. ";
      if (fileError.message) {
        errorMessage += fileError.message;
      } else {
        errorMessage += "Please try again.";
      }
      showNotification(errorMessage, "error");
    }
  }, [documentEntries, setDocumentEntries, showNotification]);

  const handleDocumentUpload = useCallback(async (index) => {
    const entry = documentEntries[index];
    if (!entry.file) {
      showNotification("Please select a file first", "error");
      return;
    }
    if (!entry.documentType) {
      showNotification("Please select a document type first", "error");
      return;
    }
    if (!ticketCode) {
      showNotification("Ticket code is required for upload", "error");
      return;
    }
    
    const newEntries = [...documentEntries];
    newEntries[index].isUploading = true;
    setDocumentEntries(newEntries);
    
    try {
      const documentFormData = new FormData();
      documentFormData.append("file", entry.file);
      // documentFormData.append("ticket_id", ticketId);
      documentFormData.append("ticket_code", ticketCode);
      documentFormData.append("sub",userId);
      if (entry.documentType === "other") {
        documentFormData.append("document_type", entry.customDocumentName);
      } else {
        documentFormData.append("document_type", entry.documentType);
      }
      
      const response = await API.post(
        "/document/upload-document/",
        documentFormData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      const updatedEntries = [...documentEntries];
      updatedEntries[index].documentCode = response.data.document_code;
      updatedEntries[index].isUploading = false;
      setDocumentEntries(updatedEntries);
      showNotification("Document uploaded successfully", "success");
    } catch (uploadError) {
      console.error("Document upload error:", uploadError.response?.data);
      const updatedEntries = [...documentEntries];
      updatedEntries[index].isUploading = false;
      setDocumentEntries(updatedEntries);
      let errorMessage = "Failed to upload document. ";
      if (uploadError.response?.status === 413) {
        errorMessage += "File size is too large. Please try a smaller file.";
      } else if (
        uploadError.response?.data?.error ||
        uploadError.response?.data?.message
      ) {
        errorMessage +=
          uploadError.response.data.error || uploadError.response.data.message;
      } else if (uploadError.message) {
        errorMessage += uploadError.message;
      } else {
        errorMessage += "Please try again.";
      }
      showNotification(errorMessage, "error");
    }
  }, [documentEntries, setDocumentEntries, ticketId, ticketCode, showNotification]);

  const handleAddDocument = useCallback(() => {
    setDocumentEntries((prev) => [
      ...prev,
      {
        documentType: "",
        file: null,
        documentCode: null,
        isUploading: false,
      },
    ]);
  }, [setDocumentEntries]);

  const handleDocumentReset = useCallback((index, keepDocumentType = true) => {
    setDocumentEntries((prevEntries) => {
      if (index < 0 || index >= prevEntries.length) return prevEntries;
      const newEntries = [...prevEntries];
      const currentDoc = newEntries[index];
      newEntries[index] = {
        documentType: keepDocumentType ? currentDoc.documentType : "",
        file: null,
        documentCode: null,
        isUploading: false,
        customDocumentName: "",
      };
      return newEntries;
    });
  }, [setDocumentEntries]);

  const handleRemoveDocument = useCallback(async (
    index,
    shouldResetInsteadOfRemove = false
  ) => {
    const documentToRemove = documentEntries[index];
    if (documentToRemove?.documentCode) {
      try {
        const res = await API.post("/document/delete-document/", {
          document_code: documentToRemove.documentCode,
          sub:userId,
        });
        if (res.status === 200) {
          showNotification("Document deleted successfully", "success");
          if (shouldResetInsteadOfRemove) {
            handleDocumentReset(index, true);
          } else {
            setDocumentEntries((prev) => prev.filter((_, i) => i !== index));
          }
        }
      } catch (err) {
        console.error("Document deletion error:", err);
        let errorMessage = "Something went wrong. Please try again.";
        if (err.response && err.response.data) {
          errorMessage =
            err.response.data.message ||
            err.response.data.error ||
            JSON.stringify(err.response.data);
        }

        showNotification(errorMessage, "error");
      }
    } else {
      setDocumentEntries((prev) => prev.filter((_, i) => i !== index));
    }
  }, [documentEntries, setDocumentEntries, showNotification, handleDocumentReset]);

  const fetchDocuments = useCallback(async (ticketCode) => {
    if (!ticketCode) return;
    
    try {
      const response = await API.get(`/document/get-documents/${ticketCode}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      if (response.data && response.data.data) {
        const existingDocuments = response.data.data;
        
        // Update document entries with existing documents
        setDocumentEntries(prevEntries => {
          const updatedEntries = [...prevEntries];
          
          existingDocuments.forEach(doc => {
            const existingIndex = updatedEntries.findIndex(
              entry => entry.documentType === doc.document_type
            );
            
            if (existingIndex !== -1) {
              // Update existing entry
              updatedEntries[existingIndex] = {
                ...updatedEntries[existingIndex],
                documentCode: doc.document_code,
                file: null, // We don't have the file object, just the reference
                fileName: doc.file_name,
                filePath: doc.file_path,
                isUploading: false,
              };
            } else {
              // Add new entry for documents not in initial entries
              updatedEntries.push({
                documentType: doc.document_type,
                file: null,
                documentCode: doc.document_code,
                fileName: doc.file_name,
                filePath: doc.file_path,
                isUploading: false,
              });
            }
          });
          
          console.log("Updated document entries:", updatedEntries);
          return updatedEntries;
        });
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      showNotification("Failed to load existing documents", "error");
    }
  }, [setDocumentEntries, showNotification]);

  // NEW: Get download URL for document viewing
  const getDocumentDownloadUrl = useCallback(async ( documentType) => {
    if (!ticketCode || !documentType) return null;
    
    try {
      const response = await API.post(
        "/document/get-download-url/",
        {
          ticket_code: ticketCode,
          sub:userId,
          document_type: documentType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const rawUrl = response.data.download_url || response.data.url;
      if (!rawUrl) return null;
      try {
        return decryptDocumentUrl(rawUrl);
      } catch (decodeError) {
        console.warn("Failed to decode document URL:", decodeError);
        return rawUrl;
      }
    } catch (error) {
      console.error("Failed to get download URL:", error);
      showNotification("Failed to get document URL", "error");
      return null;
    }
  }, [ticketCode,userId,showNotification]);

  return {
    handleDocumentChange,
    handleDocumentFileChange,
    handleDocumentUpload,
    handleAddDocument,
    handleRemoveDocument,
    fetchDocuments,
    getDocumentDownloadUrl,
  };
};

export default useDocumentHandlers;